import { convertToRaw } from 'draft-js';
import { createSubShape } from './shapeUtils';

// Texte riche dans une forme composée : celui d'une sous-forme
// (`sub.textEditorState`), d'un bloc texte libre (sous-forme de type `text`,
// sans contour, qui se place, se relie et tourne comme les autres) et le
// libellé d'un lien (`link.textEditorState`). Comme pour les box texte, le
// store tient des EditorState Draft ; ils ne deviennent du JSON brut
// (`textRaw`) qu'à l'enregistrement (cf. dataSync) et dans la banque de formes.

export const TEXT_SUB_SHAPE = 'text';

export const hasTextContent = (editorState) => !!editorState?.getCurrentContent().hasText();

export const TEXT_ALIGNS = ['left', 'center', 'right'];
export const TEXT_VERTICAL_ALIGNS = ['top', 'middle', 'bottom'];

// Mise en page du texte dans son cadre : centré dans une forme (comme dans un
// logiciel de présentation), en haut à gauche dans un bloc texte.
export const textLayoutOf = (sub) => {
  const isTextBlock = sub.shape === TEXT_SUB_SHAPE;
  return {
    align: sub.textAlign ?? (isTextBlock ? 'left' : 'center'),
    verticalAlign: sub.textVerticalAlign ?? (isTextBlock ? 'top' : 'middle'),
  };
};

const VERTICAL_JUSTIFY = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };
export const verticalJustify = (verticalAlign) => VERTICAL_JUSTIFY[verticalAlign] ?? 'center';

// Applique `mapText` (sur une copie) à chaque porteur de texte : les
// sous-formes et leurs liens.
const mapTextHolders = (shapes, mapText) => shapes.map((sub) => {
  const next = mapText({ ...sub });
  if (Array.isArray(sub.links)) next.links = sub.links.map((link) => mapText({ ...link }));
  return next;
});

// EditorState → `textRaw` (JSON de convertToRaw). Un texte vide disparaît ; un
// porteur déjà sérialisé (pas d'EditorState) garde son `textRaw`.
export const serializeShapeTexts = (shapes = []) => mapTextHolders(shapes, (holder) => {
  if (!('textEditorState' in holder)) return holder;
  const editorState = holder.textEditorState;
  delete holder.textEditorState;
  if (hasTextContent(editorState)) {
    holder.textRaw = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
  } else {
    delete holder.textRaw;
  }
  return holder;
});

// Chemin inverse : `rawToEditorState(raw)` (fourni par l'appelant, qui connaît
// le décorateur et les variables du document) recrée chaque EditorState.
export const hydrateShapeTexts = (shapes = [], rawToEditorState) => mapTextHolders(shapes, (holder) => {
  if (!holder.textRaw) return holder;
  holder.textEditorState = rawToEditorState(JSON.parse(holder.textRaw));
  delete holder.textRaw;
  return holder;
});

// Point où poser le libellé d'un lien : à mi-longueur d'une polyligne, au
// paramètre 0,5 d'une courbe de Bézier (cf. computeLinkGeometry).
export const linkLabelPoint = (geometry) => {
  if (!geometry) return null;
  const { points } = geometry;
  if (geometry.bezier) {
    const [p0, p1, p2, p3] = points;
    return [0, 1].map((axis) => (p0[axis] + 3 * p1[axis] + 3 * p2[axis] + p3[axis]) / 8);
  }
  const lengths = points.slice(1).map((p, i) => Math.hypot(p[0] - points[i][0], p[1] - points[i][1]));
  let remaining = lengths.reduce((sum, length) => sum + length, 0) / 2;
  for (let i = 0; i < lengths.length; i += 1) {
    if (remaining <= lengths[i] || i === lengths.length - 1) {
      const t = lengths[i] ? Math.min(1, remaining / lengths[i]) : 0;
      return [0, 1].map((axis) => points[i][axis] + (points[i + 1][axis] - points[i][axis]) * t);
    }
    remaining -= lengths[i];
  }
  return points[0];
};

// Bloc texte libre : posé en léger décalage de la dernière sous-forme, comme
// une forme ajoutée (cf. mutateAddSubShape), sans fond ni bordure.
export const mutateAddTextSubShape = (box, newId) => {
  const last = box.content.shapes[box.content.shapes.length - 1];
  const textBlock = createSubShape({
    id: newId,
    shape: TEXT_SUB_SHAPE,
    x: (last?.x ?? 0) + 20,
    y: (last?.y ?? 0) + 20,
    width: 160,
    height: 60,
  });
  textBlock.borders = { ...textBlock.borders, size: 0 };
  box.content.shapes.push(textBlock);
};
